import React, { useEffect } from "react";
import { IconX } from "@tabler/icons-react";

export interface MVideoPlayerProps {
  videoUrl: string;
  open: boolean;
  onClose: () => void;
  title?: string;
  autoplay?: boolean;
}

const isYouTubeShorts = (url: string): boolean =>
  /youtube\.com\/shorts\//.test(url);

const getYouTubeEmbedUrl = (url: string, autoplay: boolean): string | null => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/
  );
  if (!match) return null;

  const base = `https://www.youtube.com/embed/${match[1]}`;
  if (!autoplay) return base;
  return `${base}?autoplay=1&mute=1&playsinline=1`;
};

const MVideoPlayer: React.FC<MVideoPlayerProps> = ({
  videoUrl,
  open,
  onClose,
  autoplay = false,
}) => {
  const shorts = isYouTubeShorts(videoUrl);
  const youtubeEmbed = getYouTubeEmbedUrl(videoUrl, autoplay);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Close button — inset from safe area so it clears the notch/status bar */}
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "absolute",
          top: "calc(16px + env(safe-area-inset-top, 0px))",
          left: "calc(16px + env(safe-area-inset-left, 0px))",
          zIndex: 1,
          background: "rgba(0,0,0,0.6)",
          border: "none",
          borderRadius: "50%",
          width: 44,
          height: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          touchAction: "manipulation",
        }}
      >
        <IconX size={22} color="#fff" />
      </button>

      <div
        style={{
          width: shorts ? "min(100vw, calc(100vh * 9 / 16))" : "100vw",
          height: shorts ? "100vh" : "min(100vh, calc(100vw * 9 / 16))",
          position: "relative",
        }}
      >
        {youtubeEmbed ? (
          <iframe
            src={youtubeEmbed}
            title="video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        ) : (
          <video
            src={videoUrl}
            controls
            autoPlay={autoplay}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        )}
      </div>
    </div>
  );
};

export default MVideoPlayer;
